/**
 * Utility-Funktionen zur Typ-Konvertierung von GA4 Event- und Item-Parametern
 *
 * Stellt sicher, dass alle Parameter die korrekten Datentypen haben,
 * unabhängig davon wie sie ursprünglich übergeben wurden (String, Number, etc.)
 */

/**
 * Konvertiert GA4 Event-Level Parameter in die korrekten Datentypen
 *
 * @param {Object} params - Event-Parameter
 * @returns {Object} Konvertierte Parameter
 *
 * @example
 * const params = { value: "19.99", currency: "eur", shipping: "5.00" };
 * const converted = convertEventParameterTypes(params);
 * // { value: 19.99, currency: "EUR", shipping: 5.00 }
 */
export function convertEventParameterTypes(params) {
	if (!params || typeof params !== 'object') {
		return params;
	}

	const converted = { ...params };

	// Numeric fields (Float)
	const numericFields = ['value', 'value_netto', 'shipping', 'tax', 'discount'];
	numericFields.forEach(field => {
		if (converted[field] !== undefined && converted[field] !== null && converted[field] !== '') {
			const num = parseFloat(converted[field]);
			if (!isNaN(num)) {
				converted[field] = num;
			}
		}
	});

	// Currency (String, uppercase)
	if (converted.currency !== undefined && converted.currency !== null && converted.currency !== '') {
		converted.currency = String(converted.currency).toUpperCase();
	}

	// Transaction ID (String)
	if (converted.transaction_id !== undefined && converted.transaction_id !== null && converted.transaction_id !== '') {
		converted.transaction_id = String(converted.transaction_id);
	}

	// Search term (String)
	if (converted.search_term !== undefined && converted.search_term !== null && converted.search_term !== '') {
		converted.search_term = String(converted.search_term);
	}

	// Coupon (String)
	if (converted.coupon !== undefined && converted.coupon !== null && converted.coupon !== '') {
		converted.coupon = String(converted.coupon);
	}

	return converted;
}

/**
 * Konvertiert GA4 Item-Parameter in die korrekten Datentypen
 *
 * @param {Object} item - Item-Parameter
 * @returns {Object} Konvertiertes Item
 *
 * @example
 * const item = { price: "19.99", quantity: "2", index: "0" };
 * const converted = convertItemParameterTypes(item);
 * // { price: 19.99, quantity: 2, index: 0 }
 */
export function convertItemParameterTypes(item) {
	if (!item || typeof item !== 'object') {
		return item;
	}

	const converted = { ...item };

	// Numeric fields (Float)
	const floatFields = ['price', 'price_netto', 'discount'];
	floatFields.forEach(field => {
		if (converted[field] !== undefined && converted[field] !== null && converted[field] !== '') {
			const num = parseFloat(converted[field]);
			if (!isNaN(num)) {
				converted[field] = num;
			}
		}
	});

	// Integer fields
	const intFields = ['quantity', 'index'];
	intFields.forEach(field => {
		if (converted[field] !== undefined && converted[field] !== null && converted[field] !== '') {
			const num = parseInt(converted[field], 10);
			if (!isNaN(num)) {
				converted[field] = num;
			}
		}
	});

	// String fields
	const stringFields = [
		'item_id',
		'item_name',
		'item_brand',
		'item_category',
		'item_category2',
		'item_category3',
		'item_category4',
		'item_category5',
		'item_variant',
		'item_list_id',
		'item_list_name',
		'affiliation',
		'coupon',
		'location_id'
	];
	stringFields.forEach(field => {
		if (converted[field] !== undefined && converted[field] !== null && converted[field] !== '') {
			converted[field] = String(converted[field]);
		}
	});

	return converted;
}
